<script lang="ts">
  import Input from "$lib/components/ui/input/input.svelte";
  import * as Table from "$lib/components/ui/table";
  import { isEditActive } from "$src/application/main/mainTemplateStore";
  import {
    projectDraft,
    ProjectFormSection,
    projectInstallationAndDeliveryChanged,
    projectOriginal,
    registerErrors,
  } from "$src/application/projects/stores/projectStore";
  import {
    formatCurrency,
    formatPercent,
  } from "$src/presentation/core/currencyUtils";
  import TimeDate from "$src/presentation/core/time-date.svelte";
  import { t } from "svelte-i18n";
  import SelectDueDateType from "./select-due-date-type.svelte";
  import { Button } from "$lib/components/ui/button";
  import { Plus, Trash2 } from "lucide-svelte";
  import { type ProjectPaymentSchedule } from "$src/domain/projects/ProjectDetail";
  import {
    DueDateType,
    type PaymentSchedule,
  } from "$src/domain/projects/details/PaymentSchedule";
  import { type Project } from "$src/domain/projects/Project";
  import CurrencyInput from "$src/presentation/core/currency-input.svelte";
  import DatePicker from "$src/presentation/core/date-picker.svelte";
  import PaymentSchedulePercentDialog from "./payment-schedule-percent-dialog.svelte";
  import PaymentScheduleAssignDialog from "./payment-schedule-assign-dialog.svelte";
  import { onDestroy } from "svelte";
  import { InstallationType } from "$src/domain/projects/installations/InstallationType";
  import DueDateTypeText from "./due-date-type-text.svelte";

  export let groupStoreId: number;
  export let displayTitle: boolean = true;

  const formErrors: FormErrors = {
    noAmountError: [],
    dueDateError: [],
    dueDateTypeError: [],
    stillToPayError: [],
  };

  //let schedule: ProjectPaymentSchedule;

  let stillToPayAmount: number = 0;

  //$: schedule = $projectDraft ? $projectDraft.schedule : { payments: [] };

  const editUnsubscribe = isEditActive.subscribe((isEditing) => {
    if (isEditing) {
      if ($projectDraft && !$projectDraft.schedule)
        $projectDraft.schedule = { payments: [] };

      updateSchedule();
    }
  });

  const installationAndDeliveryChangedUnsubscribe =
    projectInstallationAndDeliveryChanged.subscribe(() => {
      console.log('ici');
      updateSchedule();
    });

  onDestroy(() => {
    if (editUnsubscribe) editUnsubscribe();
    if (installationAndDeliveryChangedUnsubscribe)
      installationAndDeliveryChangedUnsubscribe();
  });

  function addPaymentSchedule() {
    var newPayment: PaymentSchedule = {
      paymentNumber: $projectDraft.schedule.payments.length + 1,
      amount: 0,
      autoDueDateType: DueDateType.AfterOrder,
      autoDueDateDays: 15,
      paymentCompleted: false,
      comments: "",
      dueDate: undefined,
    };

    $projectDraft.schedule.payments.push(newPayment);

    $projectDraft.schedule = { ...$projectDraft.schedule };
  }

  function deletePaymentSchedule(paymentNumber: number) {
    const paymentIndex = $projectDraft.schedule.payments.findIndex(
      (payment) => payment.paymentNumber === paymentNumber
    );

    if (paymentIndex !== -1) {
      $projectDraft.schedule.payments.splice(paymentIndex, 1);

      $projectDraft.schedule.payments.forEach((payment, index) => {
        payment.paymentNumber = index + 1;
      });

      $projectDraft.schedule.payments = [...$projectDraft.schedule.payments];
    }
    updateSchedule();
  }

  function computePercent(payment: PaymentSchedule, project: Project): number {
    var totalAmount = project.prices.totalAmountIncVat;
    return (payment.amount / totalAmount) * 100;
  }

  function displayDeleteButton(payment: PaymentSchedule): boolean {
    return (
      payment.paymentNumber == $projectDraft.schedule.payments.length &&
      !payment.paymentCompleted
    );
  }

  function computeStillToPay() {
    if (!$projectDraft.schedule.payments && !$projectDraft) return;

    var total = $projectDraft.schedule.payments.reduce(
      (total, payment) => total + payment.amount,
      0
    );
    stillToPayAmount = $projectDraft.prices.totalAmountIncVat - total;
  }

  function updateSchedule() {

    console.log('update schedule');

    if (!$projectDraft) return;

    computeStillToPay();

    $projectDraft.schedule.payments = [...$projectDraft.schedule.payments];

    computeScheduleErrors();
  }

  function paymentHasError(payment: PaymentSchedule): boolean {
    return (
      payementHasNoAmountError(payment) ||
      payementHasNoDueDateError(payment) ||
      payementHasDueDateTypeError(payment)
    );
  }

  function payementHasNoAmountError(payment: PaymentSchedule): boolean {
    return payment.amount == 0;
  }

  function payementHasNoDueDateError(payment: PaymentSchedule): boolean {
    return (
      payment.autoDueDateType == DueDateType.ManualDate && !payment.dueDate
    );
  }

  function payementHasDueDateTypeError(payment: PaymentSchedule): boolean {
    return (
      (payment.autoDueDateType == DueDateType.AfterInstallation ||
        payment.autoDueDateType == DueDateType.BeforeInstallation) &&
      $projectDraft.installation.type == InstallationType.None
    );
  }

  interface FormErrors {
    [key: string]: string[] | null;
  }

  function computeScheduleErrors() {
    if (!$projectDraft.schedule.payments && !$projectDraft) return;

    if (stillToPayAmount != 0) {
      formErrors.stillToPayError = [
        $t("page.projects.detail.schedule.schedule-error"),
      ];
    } else {
      formErrors.stillToPayError = [];
    }

    let hasNoAmountError: boolean =
      $projectDraft.schedule.payments.find((p) =>
        payementHasNoAmountError(p)
      ) != undefined;
    if (hasNoAmountError) {
      formErrors.noAmountError = [
        $t("page.projects.detail.schedule.amount-notset-error"),
      ];
    } else {
      formErrors.noAmountError = [];
    }

    let hasDueDateError: boolean =
      $projectDraft.schedule.payments.find((p) =>
        payementHasNoDueDateError(p)
      ) != undefined;

    if (hasDueDateError) {
      formErrors.dueDateError = [
        $t("page.projects.detail.schedule.date-notset-error"),
      ];
    } else {
      formErrors.dueDateError = [];
    }

    let hasDueDateTypeError: boolean =
      $projectDraft.schedule.payments.find((p) =>
        payementHasDueDateTypeError(p)
      ) != undefined;

    if (hasDueDateTypeError) {
      formErrors.dueDateTypeError = [
        $t("page.projects.detail.schedule.type-noinstallation-error"),
      ];
    } else {
      formErrors.dueDateTypeError = [];
    }

    registerErrors(ProjectFormSection.Amounts, formErrors);
  }
</script>

<Table.Root class="text-right caption-top max-w-xs self-start">
  <Table.Caption class="text-md text-left">
    {#if displayTitle}
      <div class="pb-2">
        {$t("page.projects.detail.schedule.title")}
      </div>
    {/if}
    {#if $isEditActive && $projectDraft}
      <div>
        <div class="flex flex-row justify-between items-center gap-2 text-xs">
          <div>
            <Button size="sm" on:click={() => addPaymentSchedule()}>
              <Plus class="mr-2 h-3 w-3" />
              {$t("page.projects.detail.schedule.add-schedule")}
            </Button>
          </div>
        </div>
      </div>
    {/if}
  </Table.Caption>
  {#if $isEditActive && $projectDraft}
    <Table.Header class="text-xs">
      <Table.Row>
        <Table.Cell class="text-center">
          {$t("page.projects.detail.schedule.number")}
        </Table.Cell>
        <Table.Cell class="text-center">
          {$t("page.projects.detail.schedule.amount")}
        </Table.Cell>
        <Table.Cell class="text-center">
          {$t("page.projects.detail.schedule.type")}
        </Table.Cell>
        <!-- <Table.Cell class="text-center"></Table.Cell> -->
      </Table.Row>
    </Table.Header>
  {/if}
  <Table.Body>
    {#if $isEditActive && $projectDraft}
      {#each $projectDraft.schedule.payments as payment}
        <Table.Row class={payment.amount == 0 ? "border border-red-500" : ""}>
          <Table.Cell class="font-semibold align-top pt-4">
            {payment.paymentNumber}
          </Table.Cell>
          <Table.Cell class="table-cell align-top">
            <div>
              <CurrencyInput
                bind:value={payment.amount}
                currency={$projectDraft.prices.currency}
                disabled={payment.paymentCompleted}
                locale="FR"
                isNegativeAllowed={false}
                class="w-24 h-8"
                onValueChange={() => updateSchedule()}
              />
            </div>
            <div class="pt-1">
              <PaymentSchedulePercentDialog
                title="{formatPercent(
                  computePercent(payment, $projectDraft)
                )} %"
                {payment}
                amount={$projectDraft.prices.totalAmountIncVat}
                on:paymentUpdated={() => updateSchedule()}
              />
            </div>
          </Table.Cell>
          <Table.Cell class="table-cell align-top text-rigth">
            <SelectDueDateType
              disabled={payment.paymentCompleted}
              bind:value={payment.autoDueDateType}
              on:dueDateSelected={() => updateSchedule()}
            />
            {#if payment.autoDueDateType == DueDateType.ManualDate}
              <div class="pt-1">
                <DatePicker
                  bind:value={payment.dueDate}
                  on:dateSelected={() => updateSchedule()}
                />
              </div>
            {:else}
              <div
                class="flex flex-row gap-2 text-xs justify-end items-center pt-1"
              >
                <Input
                  disabled={payment.paymentCompleted}
                  type="number"
                  class="w-16 text-center"
                  min={0}
                  max={300}
                  bind:value={payment.autoDueDateDays}
                />
                {$t("page.projects.detail.schedule.days")}
              </div>
            {/if}
            {#if displayDeleteButton(payment)}
              <div class="p-2">
                <Button
                  size="sm"
                  variant="destructive"
                  on:click={() => deletePaymentSchedule(payment.paymentNumber)}
                >
                  <Trash2 class="h-4 w-4" />
                </Button>
              </div>
            {/if}
          </Table.Cell>
        </Table.Row>

        {#if paymentHasError(payment)}
          <Table.Row>
            <Table.Cell colspan={3}>
              <div class="text-red-500 text-xs">
                {#if payementHasNoAmountError(payment)}
                  {$t("page.projects.detail.schedule.amount-notset-error")}
                {/if}
              </div>
              <div class="text-red-500 text-xs">
                {#if payementHasNoDueDateError(payment)}
                  {$t("page.projects.detail.schedule.date-notset-error")}
                {/if}
              </div>
              <div class="text-red-500 text-xs">
                {#if payementHasDueDateTypeError(payment)}
                  {$t(
                    "page.projects.detail.schedule.type-noinstallation-error"
                  )}
                {/if}
              </div>
            </Table.Cell>
          </Table.Row>
        {/if}
      {/each}
      {#if stillToPayAmount != 0}
        <Table.Row>
          <Table.Cell colspan={3} class="border border-red-500">
            <div>
              <PaymentScheduleAssignDialog
                amount={stillToPayAmount}
                projectSchedule={$projectDraft.schedule}
                on:scheduleUpdated={updateSchedule}
              />
              <div class="text-red-500 text-xs">
                {$t("page.projects.detail.schedule.schedule-error")}
              </div>
            </div>
          </Table.Cell>
        </Table.Row>
      {/if}
    {:else}
      {#each $projectOriginal.schedule.payments as payment}
        <Table.Row class="border-0">
          <Table.Cell>
            {payment.paymentNumber}
          </Table.Cell>
          <Table.Cell>
            {formatPercent(computePercent(payment, $projectOriginal))} %
          </Table.Cell>
          <Table.Cell class={payment.paymentCompleted ? "" : "font-bold"}>
            {formatCurrency(payment.amount, $projectOriginal.prices.currency)}
          </Table.Cell>
        </Table.Row>
        <Table.Row class="border-b">
          <Table.Cell></Table.Cell>
          <Table.Cell
            colspan={2}
            class="lowercase text-xs text-muted-foreground italic"
          >
            {#if !payment.paymentCompleted}
              {#if payment.autoDueDateType != DueDateType.ManualDate}
                {payment.autoDueDateDays}
                {$t("page.projects.detail.schedule.days")}
                <DueDateTypeText type={payment.autoDueDateType} />
              {:else}
                <TimeDate dateString={payment.dueDate} />
              {/if}
            {:else}
              <div class="flex flex-row justify-center gap-2 items-center normal-case not-italic font-bold">
                {$t("page.projects.detail.schedule.invoice")} :
                <div>
                  <a
                    href={`#/gs/${groupStoreId}/projects/${$projectOriginal.id}`}
                  >
                    {payment.invoiceId}
                  </a>
                </div>
              </div>
            {/if}
          </Table.Cell>
        </Table.Row>
      {/each}
    {/if}
  </Table.Body>
</Table.Root>
